/**
 * Analysis API client
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6 - Analysis result API endpoints
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';
import type { ConsultNetwork } from '../utils/dummyConsultNetwork';
import type {
  AnalysisResult,
  DetailedAnalysisResult,
  ExportAnalysisResultResponse,
  ExportFormat,
  SharePlatform,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

/**
 * Analysis API methods
 */
export const analysisApi = {
  /**
   * Get analysis result by session ID
   */
  async getResult(sessionId: string): Promise<AnalysisResult> {
    const client = getApiClient();
    const response = await client.get<{ analysisResult: AnalysisResult }>(
      `/analysis/sessions/${sessionId}/result`
    );
    return response.data.analysisResult;
  },

  /**
   * Get detailed analysis result with question-answer mapping
   */
  async getDetailedResult(sessionId: string): Promise<DetailedAnalysisResult> {
    const client = getApiClient();
    const response = await client.get<{ analysisResult: DetailedAnalysisResult }>(
      `/analysis/sessions/${sessionId}/result/detailed`
    );
    return response.data.analysisResult;
  },

  /**
   * Export analysis result as PDF
   */
  async exportPdf(resultId: string): Promise<ExportAnalysisResultResponse> {
    const client = getApiClient();
    const response = await client.post<ExportAnalysisResultResponse>(
      `/analysis/results/${resultId}/export`,
      { format: 'pdf' as ExportFormat }
    );
    return response.data;
  },

  /**
   * Export analysis result as image (PNG)
   */
  async exportImage(resultId: string): Promise<ExportAnalysisResultResponse> {
    const client = getApiClient();
    const response = await client.post<ExportAnalysisResultResponse>(
      `/analysis/results/${resultId}/export`,
      { format: 'png' as ExportFormat }
    );
    return response.data;
  },

  /**
   * Generate share URL for social platforms
   */
  getShareUrl(resultId: string, platform: SharePlatform, shareText?: string): string {
    const baseUrl = window.location.origin;
    const resultUrl = `${baseUrl}/results/${resultId}`;
    const text = shareText || 'Check out my thinking analysis results!';
    const encodedText = encodeURIComponent(text);
    const encodedUrl = encodeURIComponent(resultUrl);

    switch (platform) {
      case 'twitter':
        return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      case 'line':
        return `https://line.me/R/msg/text/?${encodedText}%0A${encodedUrl}`;
      default:
        return resultUrl;
    }
  },

  /**
   * Get all company analysis results (COMPANY_WIDE permission required)
   * @param companyId Optional company ID to filter results
   */
  async getCompanyResults(companyId?: string): Promise<{ results: AnalysisResult[] }> {
    const client = getApiClient();
    const params = companyId ? { companyId } : undefined;
    const response = await client.get<{ results: AnalysisResult[] }>(
      `/analysis/company/results`,
      { params }
    );
    return response.data;
  },

  /**
   * §03つながりの構造 レベルB: 自社の相談ネットワーク(匿名化済み)を取得。
   * COMPANY_WIDE/ADMIN のみ。サーバ側で匿名ラベル化済み(実名/実IDは含まない)。
   */
  async getCompanyConsultNetwork(): Promise<{ consultNetwork: ConsultNetwork }> {
    const client = getApiClient();
    const response = await client.get<{ consultNetwork: ConsultNetwork }>(
      `/company/consult-network`
    );
    return response.data;
  },

  /**
   * Get latest analysis result for a specific user
   */
  async getLatestResult(userId: string): Promise<AnalysisResult> {
    const client = getApiClient();
    const response = await client.get<{ analysisResult: AnalysisResult }>(
      `/analysis/users/${userId}/latest`
    );
    return response.data.analysisResult;
  },

  /**
   * Find judgment pattern by exact scores
   */
  async findPatternByScores(scores: { A: number; B: number; C: number; D: number }): Promise<JudgmentPattern | null> {
    const client = getApiClient();
    try {
      const response = await client.get<{ pattern: JudgmentPattern }>(
        `/judgment-patterns/search`,
        { params: { a: scores.A, b: scores.B, c: scores.C, d: scores.D } }
      );
      return response.data.pattern;
    } catch (error: unknown) {
      // 404 means no pattern found
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          return null;
        }
      }
      throw error;
    }
  },
};

/**
 * JudgmentPattern type for analysis
 */
export interface JudgmentPattern {
  id: string;
  name: string;
  description: string;
  scoreConditions: Record<string, { min: number; max: number }>;
  desc11?: string | null;
  desc12?: string | null;
  desc13?: string | null;
  desc14?: string | null;
  desc15?: string | null;
  desc16?: string | null;
  desc21?: string | null;
  desc22?: string | null;
  desc23?: string | null;
  desc24?: string | null;
  desc25?: string | null;
  desc31?: string | null;
  desc32?: string | null;
  desc33?: string | null;
  desc34?: string | null;
  desc35?: string | null;
  desc41?: string | null;
  desc42?: string | null;
  desc43?: string | null;
  desc44?: string | null;
  desc45?: string | null;
  desc46?: string | null;
  desc47?: string | null;
  createdAt: string;
  updatedAt: string;
}
